<script setup lang="ts">
import VarsityAlert from "@/components/VarsityAlert.vue"
import VbEditToolbar from "@/components/crud/VbEditToolbar.vue"
import { VfEdit, VfFormRest, type Entity, type EntityEvent, type FormModel, type ModuleConfiguration } from "@/vf"
import { ref, type Ref } from "vue"
import { useI18n } from "vue-i18n"
import { VbBackButton } from "."

withDefaults(
    defineProps<{
        config: ModuleConfiguration
        item?: Entity
        autoRest?: boolean
        cardBodyClass?: string
        toolbar?: boolean
        header?: boolean
        cardClass?: string
        multiStep?: boolean
        schemaUrl?: string
    }>(),
    {
        toolbar: true,
        header: true,
        cardBodyClass: "card-body",
        cardClass: "card",
        autoRest: true,
        multiStep: false,
        schemaUrl: undefined,
    },
)

const emit = defineEmits<{
    (e: "saved", entity: Entity): void
    (e: "model", model: Ref<FormModel>): void
    (e: "save", event: EntityEvent): void | Promise<void>
    (e: "error", data: any): void
}>()

const { t } = useI18n()
const serverError = ref<string | null>(null)

function onError(data: any) {
    serverError.value =
        data?.message ??
        data?.detail ??
        (Array.isArray(data?._) && data._.length > 0 ? data._.join(" ") : null) ??
        t("crud:edit.error")
    emit("error", data)
}

function onSave(event: EntityEvent) {
    serverError.value = null
    emit("save", event)
}
</script>

<template>
    <VfEdit
        v-bind="{ config, item, multiStep, schemaUrl }"
        @saved="entity => emit('saved', entity)"
        @model="model => emit('model', model)"
        @save="onSave"
        @uncaught-error="onError"
    >
        <template #default="{ item, formModel, schema, additionalData, mode }">
            <div :class="cardClass">
                <div class="card-header" v-if="header && mode === 'router'">
                    <slot name="header">
                        <div class="page-header">
                            <slot name="backbutton">
                                <VbBackButton></VbBackButton>
                            </slot>
                            <h1>
                                {{ $t(config.readableName.plural) }}
                            </h1>
                            <h2></h2>
                        </div>
                    </slot>
                </div>
                <div class="card-toolbar" v-if="toolbar && mode === 'router'">
                    <VbEditToolbar :item="item" :config="config" />
                </div>
                <div :class="cardBodyClass">
                    <slot name="default" v-bind="{ item, formModel, schema, additionalData }"></slot>

                    <VfFormRest v-if="autoRest"></VfFormRest>
                    <VarsityAlert v-if="serverError" type="danger" dismissible class="mt-3" @close="serverError = null">
                        {{ serverError }}
                    </VarsityAlert>

                    <slot name="toolbar" v-bind="{ formModel }">
                        <VbEditToolbar
                            v-bind="{ item, config }"
                            :save-disabled="!!serverError"
                            v-if="toolbar && mode !== 'router'"
                        />
                    </slot>
                </div>
            </div>
        </template>
    </VfEdit>
</template>
